import { Calculator, TrendingUp, PiggyBank, ChevronRight, Cake, Scale, Landmark, PanelLeftClose, PanelLeft, Receipt, Fuel, Coins, Percent } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar } from "@/components/ui/sidebar";
import { AddCalculatorDialog } from "@/components/AddCalculatorDialog";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { CalcNotePromo } from "@/components/CalcNotePromo";
import { SearchTriggerButton } from "@/components/CalculatorSearchDialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const loanCalculators = [{
  title: "EMI Calculator",
  url: "/",
  icon: PiggyBank,
  description: "Calculate monthly loan payments"
}];

const investmentCalculators = [{
  title: "SIP Calculator",
  url: "/sip-calculator",
  icon: TrendingUp,
  description: "Plan systematic investments"
}, {
  title: "FD Calculator",
  url: "/fd-calculator",
  icon: Landmark,
  description: "Calculate fixed deposit returns"
}, {
  title: "Compound Interest",
  url: "/compound-interest-calculator",
  icon: Coins,
  description: "Calculate compound growth"
}];

const utilityCalculators = [{
  title: "General Calculator",
  url: "/calculator",
  icon: Calculator,
  description: "Quick math operations"
}, {
  title: "GST Calculator",
  url: "/gst-calculator",
  icon: Receipt,
  description: "Calculate GST, CGST, SGST & IGST"
}, {
  title: "Percentage Calculator",
  url: "/percentage-calculator",
  icon: Percent,
  description: "Calculate percent increase & discount"
}, {
  title: "Mileage Calculator",
  url: "/mileage-calculator",
  icon: Fuel,
  description: "Calculate fuel efficiency & trip cost"
}];

const otherCalculators = [{
  title: "Age Calculator",
  url: "/age-calculator",
  icon: Cake,
  description: "Calculate age & birthday countdown"
}, {
  title: "BMI Calculator",
  url: "/bmi-calculator",
  icon: Scale,
  description: "Calculate body mass index"
}];

export function AppSidebar() {
  const {
    state,
    toggleSidebar
  } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  const renderMenuItems = (items: typeof loanCalculators) => (
    <SidebarMenu>
      {items.map(item => (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton asChild tooltip={item.title}>
            <NavLink
              to={item.url}
              end
              className={cn(
                "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-200 hover:bg-primary/5",
                collapsed && "justify-center px-0"
              )}
              activeClassName="bg-primary/10 text-primary font-medium shadow-sm"
            >
              <motion.div
                initial={false}
                animate={{
                  scale: isActive(item.url) ? 1.1 : 1,
                  rotate: isActive(item.url) ? 5 : 0
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 17
                }}
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-lg transition-colors shrink-0",
                  isActive(item.url)
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-muted/50 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                )}
              >
                <item.icon className="h-4 w-4" />
              </motion.div>
              {!collapsed && (
                <div className="flex flex-1 flex-col min-w-0">
                  <span className="text-sm font-medium truncate">{item.title}</span>
                </div>
              )}
              {!collapsed && isActive(item.url) && (
                <ChevronRight className="h-4 w-4 text-primary shrink-0" />
              )}
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-border/50">
      <SidebarHeader className={cn("p-4 transition-all duration-200", collapsed && "p-2")}>
        <div className={cn("flex items-center gap-2 transition-all duration-200", collapsed ? "justify-center" : "justify-between")}>
          {!collapsed && (
            <NavLink to="/" className="flex items-center gap-3 min-w-0">
              <div className="flex flex-col min-w-0">
                <span className="text-lg font-bold tracking-tight text-foreground truncate">
                  Calculator<span className="text-primary">Sheet</span>
                </span>
                <span className="text-[10px] text-muted-foreground font-medium truncate">
                  by Utiloom
                </span>
              </div>
            </NavLink>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleSidebar}
                  className="h-8 w-8 shrink-0 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {collapsed ? "Expand sidebar" : "Collapse sidebar"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </SidebarHeader>

      <SidebarContent className={cn("transition-all duration-200", collapsed ? "px-1" : "px-2")}>
        <div className={cn("space-y-2 mb-3 transition-all duration-200", collapsed ? "px-0" : "px-2")}>
          <SearchTriggerButton collapsed={collapsed} />
          <AddCalculatorDialog collapsed={collapsed} />
        </div>

        <SidebarGroup>
          <SidebarGroupLabel className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
            Loans
          </SidebarGroupLabel>
          <SidebarGroupContent>
            {renderMenuItems(loanCalculators)}
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
            Investments
          </SidebarGroupLabel>
          <SidebarGroupContent>
            {renderMenuItems(investmentCalculators)}
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
            Utilities
          </SidebarGroupLabel>
          <SidebarGroupContent>
            {renderMenuItems(utilityCalculators)}
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
            Others
          </SidebarGroupLabel>
          <SidebarGroupContent>
            {renderMenuItems(otherCalculators)}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className={cn("space-y-3 transition-all duration-200", collapsed ? "p-2" : "p-4")}>
        {/* CalcNote Google Play Promo Card */}
        <CalcNotePromo variant="sidebar" collapsed={collapsed} />
        <FeedbackDialog collapsed={collapsed} />
      </SidebarFooter>
    </Sidebar>
  );
}